export type PriceDecimalSeparator = "period" | "comma";

export type PriceColumnNumberStatus =
  "empty" | "plain" | "normalized" | "ambiguous" | "unparseable";

export type PriceColumnNumberResult = {
  status: PriceColumnNumberStatus;
  value: string;
};

type ParsedAmount = {
  integer: string;
  fraction: string;
};

type ClassifiedNumber =
  | { kind: "plain"; amount: ParsedAmount }
  | { kind: "period"; amount: ParsedAmount }
  | { kind: "comma"; amount: ParsedAmount }
  | { kind: "ambiguous"; period: ParsedAmount; comma: ParsedAmount }
  | { kind: "invalid" };

export function priceColumnNumberIsFlagged(
  result: PriceColumnNumberResult,
): boolean {
  return result.status === "ambiguous" || result.status === "unparseable";
}

export function applyPriceColumnNumber(
  raw: string,
  columnSeparator?: PriceDecimalSeparator | null,
): string {
  return readPriceColumnNumber(raw, columnSeparator).value;
}

export function headerLooksLikePriceColumn(header: string): boolean {
  const folded = header.replace(/[_./\n]+/g, " ").trim();
  if (
    /^(?:qty|quantity|qt[eé]|uom|sku|item(?:\s*(?:no|num|#|number))?|description|desc|code|upc)$/i.test(
      folded,
    )
  ) {
    return false;
  }
  return /(?:prix|price|pricing|co[uû]ts?|cost|montant|amount|pcprc\s*\d*)/i.test(
    folded,
  );
}

export function detectPriceColumnDecimalSeparator(
  values: readonly string[],
): PriceDecimalSeparator | null {
  let periodVotes = 0;
  let commaVotes = 0;
  for (const value of values) {
    const vote = voteDecimalSeparator(value);
    if (vote === "period") periodVotes += 1;
    if (vote === "comma") commaVotes += 1;
  }
  if (periodVotes > 0 && commaVotes === 0) return "period";
  if (commaVotes > 0 && periodVotes === 0) return "comma";
  return null;
}

export function detectPriceColumnLocales(
  headers: readonly string[],
  rows: readonly (readonly string[])[],
): ReadonlyArray<PriceDecimalSeparator | null | undefined> {
  return headers.map((header, index) => {
    const values = rows.map((row) => row[index] ?? "");
    if (!columnLooksLikePrice(header, values)) return undefined;
    return detectPriceColumnDecimalSeparator(values);
  });
}

export function normalizePriceColumnRow(
  cells: readonly string[],
  locales: readonly (PriceDecimalSeparator | null | undefined)[],
): { cells: string[]; flaggedColumns: number[] } {
  const flaggedColumns: number[] = [];
  const next = cells.map((cell, index) => {
    const locale = locales[index];
    if (locale === undefined) return cell;
    const result = readPriceColumnNumber(cell, locale);
    if (priceColumnNumberIsFlagged(result) && foldPriceSpaces(cell) !== "") {
      flaggedColumns.push(index);
    }
    return result.value;
  });
  return { cells: next, flaggedColumns };
}

export function readPriceColumnNumber(
  raw: string,
  columnSeparator?: PriceDecimalSeparator | null,
): PriceColumnNumberResult {
  const trimmed = foldPriceSpaces(raw);
  if (trimmed === "") return { status: "empty", value: "" };
  const signed = readSignedBody(trimmed);
  if (signed === null) return { status: "unparseable", value: raw };
  const classified = classifyBareNumber(signed.body);
  return resolveClassified(classified, signed.negative, raw, columnSeparator);
}

function columnLooksLikePrice(
  header: string,
  values: readonly string[],
): boolean {
  if (headerLooksLikePriceColumn(header)) return true;
  const filled = values.filter((value) => foldPriceSpaces(value) !== "");
  if (filled.length < 2) return false;
  const hits = filled.filter(
    (value) => readPriceColumnNumber(value, null).status === "normalized",
  ).length;
  return hits * 2 >= filled.length;
}

function voteDecimalSeparator(raw: string): PriceDecimalSeparator | null {
  const trimmed = foldPriceSpaces(raw);
  if (trimmed === "") return null;
  const signed = readSignedBody(trimmed);
  if (signed === null) return null;
  const classified = classifyBareNumber(signed.body);
  if (classified.kind === "period" || classified.kind === "comma") {
    return classified.kind;
  }
  return null;
}

function foldPriceSpaces(value: string): string {
  return value.replace(/[\u00A0\u202F\u2007\u2009\u2060\s]+/g, " ").trim();
}

function stripEndCurrency(value: string): string {
  return value
    .replace(/^(?:[$£€¥]|US\$|CA\$|CAD|USD|EUR|GBP)\s*/i, "")
    .replace(/\s*(?:[$£€¥]|US\$|CA\$|CAD|USD|EUR|GBP)(?:\s*CA)?$/i, "")
    .trim();
}

function stripAllCurrency(value: string): string {
  let current = value;
  for (let i = 0; i < 4; i += 1) {
    const next = stripEndCurrency(current);
    if (next === current) return current;
    current = next;
  }
  return current;
}

function readSignedBody(
  value: string,
): { negative: boolean; body: string } | null {
  let negative = false;
  let body = value;
  if (body.startsWith("(") && body.endsWith(")")) {
    negative = true;
    body = body.slice(1, -1).trim();
  }
  body = stripAllCurrency(body);
  if (body.startsWith("+")) {
    body = body.slice(1).trim();
  } else if (body.startsWith("-") || body.startsWith("\u2212")) {
    negative = true;
    body = body.slice(1).trim();
  }
  body = stripAllCurrency(body);
  if (/[$£€¥A-Za-z]/.test(body)) return null;
  return { negative, body };
}

function classifyBareNumber(body: string): ClassifiedNumber {
  if (body === "" || !/^[\d., ]+$/.test(body)) return { kind: "invalid" };
  const hasSpace = body.includes(" ");
  const hasComma = body.includes(",");
  const hasPeriod = body.includes(".");
  if (hasSpace) return classifySpaced(body, hasComma, hasPeriod);
  if (hasComma && hasPeriod) return classifyBothMarks(body);
  if (hasComma) return classifySingleMark(body, ",");
  if (hasPeriod) return classifySingleMark(body, ".");
  if (/^\d+$/.test(body)) {
    return {
      kind: "plain",
      amount: { integer: stripLeadingZeros(body), fraction: "" },
    };
  }
  return { kind: "invalid" };
}

function classifySpaced(
  body: string,
  hasComma: boolean,
  hasPeriod: boolean,
): ClassifiedNumber {
  if (hasComma && hasPeriod) return { kind: "invalid" };
  const groups = body.split(" ");
  if (groups.some((group) => group === "")) return { kind: "invalid" };
  if (hasComma || hasPeriod) {
    return classifySpacedDecimal(groups, hasComma ? "," : ".");
  }
  return classifySpacedInteger(groups);
}

function classifySpacedInteger(groups: string[]): ClassifiedNumber {
  if (!groups.every(isDigitRun)) return { kind: "invalid" };
  if (groups.length > 1 && !spaceThousandsOk(groups))
    return { kind: "invalid" };
  return {
    kind: "plain",
    amount: { integer: stripLeadingZeros(groups.join("")), fraction: "" },
  };
}

function classifySpacedDecimal(
  groups: string[],
  mark: "," | ".",
): ClassifiedNumber {
  const split = splitLastGroup(groups, mark);
  if (split === null) return { kind: "invalid" };
  if (!spacedDecimalHeadOk(split.head, split.fraction)) {
    return { kind: "invalid" };
  }
  return {
    kind: mark === "," ? "comma" : "period",
    amount: {
      integer: stripLeadingZeros(split.head.join("") || "0"),
      fraction: split.fraction,
    },
  };
}

function splitLastGroup(
  groups: string[],
  mark: "," | ".",
): { head: string[]; fraction: string } | null {
  const last = groups[groups.length - 1] ?? "";
  const lastParts = last.split(mark);
  if (lastParts.length !== 2) return null;
  const [lastInteger, fraction] = lastParts;
  if (lastInteger === undefined || fraction === undefined) return null;
  return { head: [...groups.slice(0, -1), lastInteger], fraction };
}

function spacedDecimalHeadOk(head: string[], fraction: string): boolean {
  if (!head.every(isDigitRun)) return false;
  if (fraction !== "" && !isDigitRun(fraction)) return false;
  if (head.length > 1 && !spaceThousandsOk(head)) return false;
  return true;
}

function spaceThousandsOk(groups: string[]): boolean {
  const [first, ...rest] = groups;
  if (first === undefined || first.length < 1 || first.length > 3) return false;
  return rest.every((group) => group.length === 3);
}

function classifyBothMarks(body: string): ClassifiedNumber {
  const lastComma = body.lastIndexOf(",");
  const lastPeriod = body.lastIndexOf(".");
  if (lastComma > lastPeriod) {
    return splitMixed(body, lastComma, ".", "comma");
  }
  return splitMixed(body, lastPeriod, ",", "period");
}

function splitMixed(
  body: string,
  decimalIndex: number,
  thousandMark: "," | ".",
  kind: "period" | "comma",
): ClassifiedNumber {
  const integerRaw = body.slice(0, decimalIndex);
  const fraction = body.slice(decimalIndex + 1);
  if (!fractionDigitsOk(fraction)) return { kind: "invalid" };
  const fromThousands = mixedThousandsAmount(
    integerRaw,
    thousandMark,
    fraction,
  );
  if (fromThousands) return { kind, amount: fromThousands };
  return mixedPlainInteger(integerRaw, fraction, kind);
}

function fractionDigitsOk(fraction: string): boolean {
  return fraction === "" || isDigitRun(fraction);
}

function mixedThousandsAmount(
  integerRaw: string,
  thousandMark: "," | ".",
  fraction: string,
): ParsedAmount | null {
  const thousandParts = integerRaw.split(thousandMark);
  if (thousandParts.length <= 1) return null;
  const joined = readThousandsGroups(thousandParts);
  if (!joined) return null;
  return { integer: joined.integer, fraction };
}

function mixedPlainInteger(
  integerRaw: string,
  fraction: string,
  kind: "period" | "comma",
): ClassifiedNumber {
  if (integerRaw !== "" && !isDigitRun(integerRaw)) return { kind: "invalid" };
  if (integerRaw.includes(",") || integerRaw.includes(".")) {
    return { kind: "invalid" };
  }
  return {
    kind,
    amount: {
      integer: stripLeadingZeros(integerRaw === "" ? "0" : integerRaw),
      fraction,
    },
  };
}

function classifySingleMark(body: string, mark: "," | "."): ClassifiedNumber {
  const parts = body.split(mark);
  const thousands = readThousandsGroups(parts);
  const decimal = readDecimalParts(parts);
  if (thousands && decimal) {
    return {
      kind: "ambiguous",
      period: mark === "," ? thousands : decimal,
      comma: mark === "," ? decimal : thousands,
    };
  }
  if (thousands) {
    return {
      kind: mark === "," ? "period" : "comma",
      amount: thousands,
    };
  }
  if (decimal) {
    return {
      kind: mark === "," ? "comma" : "period",
      amount: decimal,
    };
  }
  return { kind: "invalid" };
}

function readThousandsGroups(parts: string[]): ParsedAmount | null {
  if (parts.length < 2 || !parts.every(isDigitRun)) return null;
  const [first, ...rest] = parts;
  if (first === undefined || first.length < 1 || first.length > 3) return null;
  if (!rest.every((group) => group.length === 3)) return null;
  return { integer: stripLeadingZeros(parts.join("")), fraction: "" };
}

function readDecimalParts(parts: string[]): ParsedAmount | null {
  if (parts.length !== 2) return null;
  const [integerPart, fraction] = parts;
  if (!decimalHalvesOk(integerPart, fraction)) return null;
  return {
    integer: stripLeadingZeros(integerPart === "" ? "0" : integerPart),
    fraction: fraction ?? "",
  };
}

function decimalHalvesOk(
  integerPart: string | undefined,
  fraction: string | undefined,
): boolean {
  if (integerPart === undefined || fraction === undefined) return false;
  if (integerPart !== "" && !isDigitRun(integerPart)) return false;
  if (fraction !== "" && !isDigitRun(fraction)) return false;
  return integerPart !== "" || fraction !== "";
}

function isDigitRun(value: string): boolean {
  return /^\d+$/.test(value);
}

function stripLeadingZeros(digits: string): string {
  const stripped = digits.replace(/^0+(?=\d)/, "");
  return stripped === "" ? "0" : stripped;
}

function formatPlain(amount: ParsedAmount, negative: boolean): string {
  const sign = negative ? "-" : "";
  if (amount.fraction === "") return `${sign}${amount.integer}`;
  return `${sign}${amount.integer}.${amount.fraction}`;
}

function resolveClassified(
  classified: ClassifiedNumber,
  negative: boolean,
  raw: string,
  columnSeparator?: PriceDecimalSeparator | null,
): PriceColumnNumberResult {
  if (classified.kind === "invalid") {
    return { status: "unparseable", value: raw };
  }
  if (classified.kind === "ambiguous") {
    return resolveAmbiguous(classified, negative, raw, columnSeparator);
  }
  return finishAmount(formatPlain(classified.amount, negative), raw);
}

function resolveAmbiguous(
  classified: Extract<ClassifiedNumber, { kind: "ambiguous" }>,
  negative: boolean,
  raw: string,
  columnSeparator?: PriceDecimalSeparator | null,
): PriceColumnNumberResult {
  if (columnSeparator === "period") {
    return finishAmount(formatPlain(classified.period, negative), raw);
  }
  if (columnSeparator === "comma") {
    return finishAmount(formatPlain(classified.comma, negative), raw);
  }
  return { status: "ambiguous", value: raw };
}

function finishAmount(formatted: string, raw: string): PriceColumnNumberResult {
  if (formatted === foldPriceSpaces(raw)) {
    return { status: "plain", value: formatted };
  }
  return { status: "normalized", value: formatted };
}
